import * as Sentry from '@sentry/react';

import { Config as config } from '@nw/shared/config';

import { getUserInfoDetails } from './UserInfoUtils';

const isNotDevEnvironment =
  process.env.NODE_ENV !== 'development' ||
  window.location.hostname !== 'localhost';

const sdkIssues = [
  /**
   * @issue https://github.com/getsentry/sentry-javascript/issues/15065
   * @description Java exception was raised during method invocation
   * @ignoreReason This is a known issue with Sentry SDK 9.2.0
   * @todo Remove this ignore when upgrading to Sentry SDK > 9.2.0
   */
  'Java exception was raised during method invocation'
];

const ignoreErrors = [
  'ResizeObserver loop limit exceeded',
  'getReadModeExtract',
  'getReadModeConfig',
  'getReadModeRender',
  'ServiceWorker script evaluation failed',
  'Identifier "originalPrompt" has already been declared',
  'Cannot redefine property: googletag',
  'vid_mate_check is not defined',
  'privateSpecialRepair is not defined',
  'postMessage',
  'Loading chunk',
  'Loading CSS chunk',
  'shouldFetchOnWindowFocus',
  'shouldFetchOnReconnect',
  `Unexpected token 'F', "Failed to fetch"`,
  ...sdkIssues
];

const denyUrls = [
  'https://www.googletagmanager.com',
  'https://connect.facebook.net/en_US/fbevents.js'
];

function shouldIgnoreError(errorMessage: Error | string) {
  let ignoreError = false;
  ignoreErrors.forEach((errorText) => {
    if (errorMessage.toString().includes(errorText)) {
      ignoreError = true;
    }
  });
  return ignoreError;
}

function beforeSendSentryIssue(
  event: Sentry.Event,
  hint: Sentry.EventHint | undefined
) {
  const uiClickBreadcrumbs =
    event &&
    event.breadcrumbs &&
    event.breadcrumbs.filter(
      (breadcrumb) => breadcrumb.category === 'ui.click'
    );

  const breadcrumbs =
    event &&
    event.breadcrumbs &&
    event.breadcrumbs.filter(
      (breadcrumb) =>
        breadcrumb.category !== 'console' &&
        !uiClickBreadcrumbs?.slice(0, -3).includes(breadcrumb)
    );

  event.breadcrumbs = breadcrumbs;

  if (hint) {
    // Filter out some errors we don't care about
    const error = hint.originalException;
    let errorMessage = '';
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    if (shouldIgnoreError(errorMessage)) {
      return null;
    }
  }

  return event;
}

export const setUserDetailsInSentry = (providedUserId?: string): void => {
  const userInfo = getUserInfoDetails();
  const currentUserId: string = providedUserId || userInfo.userId || 'GUEST';
  Sentry.setUser({ id: currentUserId, user_details: getUserInfoDetails() });
};

export const removeUserDetailsInSentry = (): void => {
  const scope = Sentry.getCurrentScope();
  scope.setUser(null);
};

export const initializeSentry = (): void => {
  if (isNotDevEnvironment) {
    Sentry.init({
      dsn: config.SENTRY_DSN,
      integrations: function (integrations) {
        const filteredIntegrations = integrations.filter(
          (i) => i.name !== 'GlobalHandlers' && i.name !== 'ReportingObserver'
        );
        return [...filteredIntegrations];
      },
      tracesSampleRate: 1.0,
      environment: config.SENTRY_ENVIRONMENT,
      ignoreErrors: ignoreErrors,
      denyUrls: denyUrls,
      release: config.SENTRY_RELEASE_VERSION,
      beforeSend: beforeSendSentryIssue
    });

    setUserDetailsInSentry();
  }
};

function getErrorMessage(e): string {
  return e && e.stack && e.message;
}

export function captureSentryException(
  error: Error,
  errorInfo: any = undefined,
  tags: any = undefined
): void {
  if (error && isNotDevEnvironment) {
    let updatedError = error;
    if (!getErrorMessage(error)) {
      updatedError = new Error(error.toString());
    }
    if (errorInfo) {
      const scope = Sentry.getCurrentScope();
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });
    }
    if (tags) {
      Object.keys(tags).forEach((key) => {
        Sentry.setTag(key, tags[key]);
      });
    }

    setUserDetailsInSentry();

    const ignoreError = shouldIgnoreError(error);
    if (!ignoreError) {
      Sentry.captureException(updatedError);
    }
  }
}

export const entryAddBreadCrumb = (state: string) => () => {
  Sentry.addBreadcrumb({
    type: 'info',
    category: 'machine.transitions',
    message: `${state} entered`
  });
};

export const exitAddBreadCrumb = (state: string) => () => {
  Sentry.addBreadcrumb({
    type: 'info',
    category: 'machine.transitions',
    message: `${state} exited`
  });
};

export const serviceAddBreadCrumb =
  (state: string) => (context: any, event: any) => {
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'machine.transitions',
      message: `In ${state}`,
      data: { context, event }
    });
  };
